import { EventType } from "@/analytics/analytics-events";
import { DownloadImageProperties } from "@/analytics/generic-events-properties";
import { ElementIconType } from "@/components/ui/icons";
import { RootState } from "@/store/store";
import { useAppSelector } from "@/store/store-hooks";
import {
  downloadFile,
  downloadFileAsBlob,
  downloadFileWithDownloadManager,
  downloadLargeFile,
} from "@/utils/download";
import {
  convertToDateString,
  createSignedUrlForIElementUri,
  IElementsState,
  selectAncestor,
  selectChildDepthFirst,
  selectIElement,
} from "@faro-lotv/app-component-toolbox";
import { DownloadIcon, FaroMenuItem } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import {
  GUID,
  IElement,
  IElementGenericPointCloud,
  IElementPointCloudE57,
  IElementPointCloudGeoSlam,
  IElementPointCloudLaz,
  IElementVideo360,
  isIElementBimImportGroup,
  isIElementBimModelSection,
  isIElementGenericPointCloud,
  isIElementImg2d,
  isIElementImg360,
  isIElementModel3d,
  isIElementPointCloudE57Flash,
  isIElementPointCloudGeoSlam,
  isIElementPointCloudLaz,
  isIElementRoomSection,
  isIElementSectionGeoslam,
} from "@faro-lotv/ielement-types";
import { ProjectApi, useApiClientContext } from "@faro-lotv/service-wires";
import { type JSX, useCallback } from "react";
import {
  ContextMenuAction,
  ContextMenuActionType,
  NestedMenuHandlerFnArgs,
  NestedMenuObject,
} from "../action-types";
import {
  selectDownloadablePointCloud,
  selectDownloadableVideoWalk,
} from "../utils";

function nestedMenuProps(elementId: GUID, state: RootState): NestedMenuObject {
  const iElement = selectIElement(elementId)(state);
  const hasFlashScans = selectChildDepthFirst(
    iElement,
    isIElementPointCloudE57Flash,
  )(state);

  return {
    title: "select data to download",
    options: [
      ...(hasFlashScans
        ? [
            {
              label: "Download SLAM and Flash Scans",
              tagline: "E57 file - Structured",
              onClick: ({
                menuItemId,
                state,
                projectApi,
              }: NestedMenuHandlerFnArgs) =>
                handlePointCloudDownload<IElementPointCloudE57>(
                  menuItemId,
                  state,
                  projectApi,
                  isIElementPointCloudE57Flash,
                ),
            },
          ]
        : []),
      {
        label: "Download SLAM point cloud",
        tagline: "LAZ file - Unstructured",
        onClick: ({ menuItemId, state, projectApi }: NestedMenuHandlerFnArgs) =>
          handlePointCloudDownload<IElementPointCloudLaz>(
            menuItemId,
            state,
            projectApi,
            isIElementPointCloudLaz,
          ),
      },
      {
        label: "Download Raw scan file",
        tagline: "GEOSLAM file",
        onClick: ({ menuItemId, state, projectApi }: NestedMenuHandlerFnArgs) =>
          handlePointCloudDownload<IElementPointCloudGeoSlam>(
            menuItemId,
            state,
            projectApi,
            isIElementPointCloudGeoSlam,
          ),
      },
    ],
    shouldShowNestedMenu(elementID: GUID, state: RootState) {
      const iElement = selectIElement(elementID)(state);
      return !!selectChildDepthFirst(iElement, isIElementSectionGeoslam)(state);
    },
  };
}

export const DOWNLOAD_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.download,
  label: "Download point cloud",
  icon: ElementIconType.DownloadIcon,
  nestedMenuProps,
  handler: ({ elementID, state, apiClients }) =>
    handlePointCloudDownload<IElementGenericPointCloud>(
      elementID,
      state,
      apiClients.projectApiClient,
      isIElementGenericPointCloud,
    ),
};

type DownloadVideoWalkActionProps = {
  /** Id of the element in the project tree. */
  elementId: GUID;
  /** Whether to use dark mode styling */
  dark: boolean;
  /** Callback to close the context menu after the action is performed */
  closeContextMenu?(): void;
};

/**
 * @returns a menu item that downloads the video of a trajectory
 */
export function DownloadVideoWalkAction({
  elementId,
  dark,
  closeContextMenu,
}: DownloadVideoWalkActionProps): JSX.Element {
  const element = useAppSelector(selectIElement(elementId));

  const videoMode = useAppSelector(selectDownloadableVideoWalk(element));

  const { projectApiClient } = useApiClientContext();

  const downloadVideoWalk = useCallback(() => {
    Analytics.track(EventType.downloadVideoWalk);
    handleVideoWalkDownload(videoMode, projectApiClient);
    closeContextMenu?.();
  }, [projectApiClient, videoMode, closeContextMenu]);

  return (
    <FaroMenuItem
      label="Download Video Walk"
      label-aria="Download Video Walk"
      Icon={DownloadIcon}
      dark={dark}
      onClick={downloadVideoWalk}
    />
  );
}

export const DOWNLOAD_VIDEO_WALK_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.downloadVideoWalk,
  CustomComponent: DownloadVideoWalkAction,
};

export const DOWNLOAD_IMAGE_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.downloadImage,
  label: "Download image",
  icon: ElementIconType.DownloadIcon,
  nestedMenuProps,
  handler: ({ elementID, state, apiClients }) =>
    downloadImage(elementID, state.iElements, apiClients.projectApiClient),
};

/**
 * This function handle downloads of original CAD file uploaded by user to the project
 *
 * @param cadModelStreamId ID of the 3D Model from whom CAD should be downloaded.
 * @param state State of the application.
 * @param projectApi API client to use for signing the download url.
 */
export async function handleCadDownload(
  cadModelStreamId: string,
  state: RootState,
  projectApi: ProjectApi,
): Promise<void> {
  const cadModelElement = selectIElement(cadModelStreamId)(state);

  const bimModelSection = selectAncestor(
    cadModelElement,
    isIElementBimModelSection,
  )(state);

  if (bimModelSection) {
    const bimModelGroup = selectChildDepthFirst(
      bimModelSection,
      isIElementBimImportGroup,
    )(state);

    if (bimModelGroup) {
      const bimModel = selectChildDepthFirst(
        bimModelGroup,
        isIElementModel3d,
      )(state);

      if (bimModel && isIElementModel3d(bimModel)) {
        const signedUrl = await createSignedUrlForIElementUri(
          projectApi,
          bimModel,
        ).requestSignedUrl();

        downloadFile(signedUrl, bimModel.fileName ?? undefined);
      }
    }
  }
}

/**
 * @param elementID ID of the element from whom the point cloud should be downloaded.
 * @param state State of the application.
 * @param projectApi API client to use for signing the download url.
 * @param predicate Selection typeguard for the point cloud
 */
async function handlePointCloudDownload<Type extends IElementGenericPointCloud>(
  elementID: GUID,
  state: RootState,
  projectApi: ProjectApi,
  predicate: (el: IElement) => el is Type,
): Promise<void> {
  Analytics.track(EventType.downloadPointCloud);
  // Get the pointcloud section element.
  const iElement = selectIElement(elementID)(state);

  // Get the child element of the section, that is a generic pointcloud and contains the url and the file name.
  const pointCloud = selectDownloadablePointCloud(iElement, predicate)(state);

  if (!pointCloud) return;

  const signedUrl = await createSignedUrlForIElementUri(
    projectApi,
    pointCloud,
  ).requestSignedUrl();

  // Download the file and assign the file name.
  downloadLargeFile(signedUrl, pointCloud.fileName ?? undefined);
}

/**
 * @param videoWalk The video walk element to be downloaded.
 * @param projectApi API client to use for signing the download url.
 * @param openToast Function to display toast notifications.
 */
async function handleVideoWalkDownload(
  videoWalk: IElementVideo360 | undefined,
  projectApi: ProjectApi,
): Promise<void> {
  if (!videoWalk) return;

  const signedUrl = await createSignedUrlForIElementUri(
    projectApi,
    videoWalk,
  ).requestSignedUrl();

  // Download the file using the manager so that we can avoid the
  // default MP4 management of the browser
  downloadFileWithDownloadManager(signedUrl);
}

/**
 * Downloads a 360 image file for a given element, using a secure signed URL.
 *
 * The downloaded file name is constructed from the parent element's name,
 * an underscore, the creation date (formatted as MM-DD-YYYY), and the image's original file extension.
 *
 * @param elementId - The ID of the target element.
 * @param iElements the iElements store slice
 * @param projectApi - Instance of the API client for backend communication.
 */
async function downloadImage(
  elementId: GUID,
  iElements: IElementsState,
  projectApi: ProjectApi,
): Promise<void> {
  // Get the element
  const imageElement = selectIElement(elementId)({ iElements });

  assert(imageElement, "Image element not found");

  // Track the download event for analytics
  Analytics.track<DownloadImageProperties>(EventType.downloadImage, {
    imageType: isIElementImg2d(imageElement) ? "2D" : "360",
  });

  // for 2D image, get the room section parent. In case of 360, imageElement is the parent section of the 360 element
  const parentElement = isIElementImg2d(imageElement)
    ? selectAncestor(imageElement, isIElementRoomSection)({ iElements })
    : imageElement;

  // For pano 360 case, find the first child element of type IElementImg360 using a depth-first search
  const image = isIElementImg2d(imageElement)
    ? imageElement
    : selectChildDepthFirst(parentElement, isIElementImg360)({ iElements });

  // image is not found, assert to prevent further execution
  assert(parentElement && image, "360 image element not found");

  // Extract the file extension from the original image name (e.g., ".jpeg" or ".png")
  const dotIndex = image.name.lastIndexOf(".");
  // In some old project the image name may not have an extension, but we know for those projects the extension is always .jpg
  // For 2D photo, the extension is attached with a series of number digits ex. ".jpg1450893". The digits needs to be removed.
  const ext =
    dotIndex < 0
      ? ".jpg"
      : image.name.substring(dotIndex).replaceAll(/\d/g, "");

  // Format the creation date and replace slashes with dashes (e.g., "02-27-2024")
  const imageDate = convertToDateString(image.createdAt).replace(/\//g, "-");

  // Build the final file name: <element name>_<formatted date><extension>
  const finalFilename = `${parentElement.name}_${imageDate}${ext}`;

  // Generate a secure signed URL to access the image file
  const signedUrl = await createSignedUrlForIElementUri(
    projectApi,
    image,
  ).requestSignedUrl();

  // Trigger the actual image download using the signed URL and the final filename
  await downloadFileAsBlob(signedUrl, finalFilename);
}
